import { useEffect, useSyncExternalStore } from 'react';
import { reloadPage } from '../lib/reloadPage.js';
import { getPhrasesLessonsStatus, loadPhrasesLessons, subscribePhrasesLessons } from './lessons.js';

/**
 * React side of the lazy phrases lessons (lessons.ts). Mounting a screen
 * that needs them starts the load; every subscriber re-renders when it
 * lands, so `getLesson(phraseId)` / `getCourseTree(PHRASES_COURSE_ID)` can
 * simply be read again during render.
 *
 *   'loading' — not loaded yet (also before the load was ever asked for)
 *   'ready'   — the phrases lessons are in the index
 *   'error'   — the load failed (offline before the chunk was cached, a
 *               chunk gone after a deploy): show a way out — `retry()`
 */

export type PhrasesContentStatus = 'loading' | 'ready' | 'error';

export interface PhrasesContent {
  status: PhrasesContentStatus;
  /** Try again (`retryPhrasesLessons`): in place first, then a page reload
   *  only when that fails AND the device is online. */
  retry: () => void;
}

/**
 * Try the phrases download again.
 *
 *   1. In place first. Firefox and Safari do not keep a failed dynamic
 *      import, so this alone recovers them — no reload, nothing in memory
 *      lost. The screen shows loading → ready (or → error again).
 *   2. If that fails too and the device is ONLINE, reload the page. Chromium
 *      keeps a failed dynamic import for the life of the page (the module
 *      map remembers it), so only a fresh page can fetch the chunk again;
 *      the screen starts the load on mount.
 *   3. OFFLINE: never reload. The chunk only fails offline when no service
 *      worker controls the page (a first visit before the precache is done,
 *      an in-app webview, Firefox private mode); there a reload cannot bring
 *      the app back — the browser shows its own offline page and the whole
 *      app is gone, with anything held only in memory (e.g. a paused lesson
 *      completion). The error screen stays; the learner can go back to the
 *      main course, or tap again once the signal is back.
 */
export function retryPhrasesLessons(): void {
  loadPhrasesLessons().catch(() => {
    if (navigator.onLine !== false) reloadPage();
  });
}

/** `enabled = false` only reads the status — it never starts the download
 *  (e.g. Home's ring, unless today has a phrases lesson). */
export function usePhrasesContent(enabled = true): PhrasesContent {
  const raw = useSyncExternalStore(subscribePhrasesLessons, getPhrasesLessonsStatus, getPhrasesLessonsStatus);

  useEffect(() => {
    if (!enabled) return;
    // The failure is reported through the status ('error'), not thrown.
    loadPhrasesLessons().catch(() => undefined);
  }, [enabled]);

  const status: PhrasesContentStatus = raw === 'ready' ? 'ready' : raw === 'error' ? 'error' : 'loading';
  return { status, retry: retryPhrasesLessons };
}
